/**
 * @fileoverview useTransactionForm - Transaction add form state management.
 *
 * Manages all state for the "Add Transaction" form including transaction fields,
 * categories, adjustments, and user tracking. Part of the transaction hooks
 * composition pattern.
 *
 * @description This hook provides:
 * - Form field state management
 * - Transaction type-specific state (expense/income/adjustment/transfer)
 * - Category allocation management
 * - Adjustment category tracking
 * - User/paidBy tracking
 * - Form reset capability
 * - Input validation helpers (positive numbers, decimal limits)
 *
 * Form structure:
 * - Common fields: type, amount, description, date, currentUser
 * - Expense/Income/Transfer: transactionCategories array
 * - Adjustment: adjustmentFromCategory, adjustmentToCategory
 * - Income-specific: useTemplateForIncome flag
 * - Expense-specific: paidDirectly flag
 *
 * @module hooks/useTransactionForm
 */
import { useState, useEffect, useRef } from 'react';
import { getTodaysDate } from '../utils/dateUtils';

/**
 * Hook for managing transaction form state.
 *
 * @param {Array<Object>} users - List of user objects with userKey and userName
 * @param {string} loggedInUserName - Name of the currently logged-in user
 * @returns {Object} Form state and operations
 * @returns {string} return.transactionType - Selected transaction type
 * @returns {string} return.amount - Transaction amount
 * @returns {string} return.description - Transaction description
 * @returns {string} return.date - Transaction date (YYYY-MM-DD)
 * @returns {Array} return.transactionCategories - Category allocations
 * @returns {number|null} return.currentUserKey - User key of who initiated transaction
 * @returns {boolean} return.paidDirectly - For expenses: whether paid outside budget
 * @returns {boolean} return.useTemplateForIncome - For income: use template allocation
 * @returns {string} return.adjustmentFromCategory - For adjustments: source category
 * @returns {string} return.adjustmentToCategory - For adjustments: destination category
 * @returns {Function} return.resetForm - Reset all fields to defaults
 * @returns {Function} return.handlePositiveNumberInput - Enforce positive numbers
 *
 * @example
 * const form = useTransactionForm(users, loggedInUserName);
 * form.setTransactionType('Expense');
 * form.handlePositiveNumberInput('123.45', form.setAmount);
 */
export const useTransactionForm = (users, loggedInUserName) => {
    // Form state for adding transactions
    const [transactionType, setTransactionType] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(getTodaysDate());
    const [transactionCategories, setTransactionCategories] = useState([
        { category: '', amount: '' }
    ]);

    // Adjustment-specific state
    const [adjustmentFromCategory, setAdjustmentFromCategory] = useState('');
    const [adjustmentToCategory, setAdjustmentToCategory] = useState('');

    // User management state
    const [paidDirectly, setPaidDirectly] = useState(false);
    const [useTemplateForIncome, setUseTemplateForIncome] = useState(false);

    // Default to logged-in user's key if available, otherwise first user's key
    // users is an array of objects: { userKey, userName }
    const getDefaultUserKey = () => {
        if (!users || users.length === 0) return null;
        const loggedInUser = users.find(u => u.userName === loggedInUserName);
        return loggedInUser ? loggedInUser.userKey : (users[0]?.userKey || null);
    };
    const [currentUserKey, setCurrentUserKey] = useState(getDefaultUserKey);

    // Auto-set currentUserKey when users array becomes available or changes
    // This ensures a default is set even when the dropdown is hidden (single user, singleBankMode, etc.)
    // Also sets the default to logged-in user when multiple users exist
    useEffect(() => {
        if (users && users.length > 0) {
            // Always update to the correct default when users array is available
            // This handles both initial load and when the logged-in user should be pre-selected
            const defaultKey = getDefaultUserKey();
            if (currentUserKey === null || currentUserKey === undefined) {
                setCurrentUserKey(defaultKey);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, loggedInUserName]);

    // Track if this is the initial mount to avoid auto-filling on mount
    const isInitialMount = useRef(true);

    // Auto-fill first category amount when total amount changes
    useEffect(() => {
        // Skip on initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Only auto-fill for Expense and Income types (not Transfer, Adjustment, or template income)
        if ((transactionType === 'Expense' || transactionType === 'Income') && !useTemplateForIncome) {
            if (amount && transactionCategories.length >= 1) {
                const sumOfOtherCategories = transactionCategories
                    .slice(1) // Skip first category
                    .reduce((sum, cat) => sum + (parseFloat(cat.amount) || 0), 0);

                const firstCategoryAmount = parseFloat(amount) - sumOfOtherCategories;
                const newAmount = firstCategoryAmount >= 0 ? firstCategoryAmount.toFixed(2) : '0.00';

                // Only update if the amount actually changed
                if (transactionCategories[0].amount !== newAmount) {
                    setTransactionCategories(prev => [
                        { ...prev[0], amount: newAmount },
                        ...prev.slice(1)
                    ]);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amount, transactionType, useTemplateForIncome]);

    // Reset form to initial state
    const resetForm = () => {
        setAmount('');
        setDescription('');
        setTransactionCategories([{ category: '', amount: '' }]);
        setAdjustmentFromCategory('');
        setAdjustmentToCategory('');
        setUseTemplateForIncome(false);
        setDate(getTodaysDate());
        setTransactionType('');
        setPaidDirectly(false);
        // Reset to logged-in user's key if available, otherwise first user's key
        setCurrentUserKey(getDefaultUserKey());
    };

    // Enforce positive numbers only
    const handlePositiveNumberInput = (value, setter) => {
        const cleanValue = value.replace(/^-+/, '');

        if (cleanValue === '') {
            setter('');
            return;
        }

        if (cleanValue.includes('.')) {
            const [_whole, decimal] = cleanValue.split('.');
            if (decimal && decimal.length > 2) {
                return;
            }
        }

        setter(cleanValue);
    };

    return {
        // Form state
        transactionType,
        setTransactionType,
        amount,
        setAmount,
        description,
        setDescription,
        date,
        setDate,
        transactionCategories,
        setTransactionCategories,
        currentUserKey,
        setCurrentUserKey,
        paidDirectly,
        setPaidDirectly,
        useTemplateForIncome,
        setUseTemplateForIncome,

        // Adjustment state
        adjustmentFromCategory,
        setAdjustmentFromCategory,
        adjustmentToCategory,
        setAdjustmentToCategory,

        // Actions
        resetForm,
        handlePositiveNumberInput
    };
};
